"use client";

import React from "react";
import { getSession } from "@/lib/auth-client";

export type User = {
  name: string;
  email: string;
  avatar: string;
};

/**
 * Conditional component that renders different content based on user authentication state
 * @param userContent - Function to render content when user is authenticated
 * @param noUserContent - Content to render when user is not authenticated
 */
export function UserOrNot({
  userContent,
  noUserContent,
}: {
  userContent: (user: User) => React.ReactNode;
  noUserContent: React.ReactNode;
}) {
  // State for user data and loading status
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  // Fetch user session data on component mount
  React.useEffect(() => {
    const fetchUser = async () => {
      const session = await getSession();
      if (session?.data?.user) {
        setUser({
          name: session.data.user.name || "User",
          email: session.data.user.email || "",
          avatar: session.data.user.image || "",
        });
      }
      setIsLoading(false);
    };
    fetchUser();
  }, []);

  // Show nothing while loading
  if (isLoading) {
    return null;
  }

  // Show noUserContent if user is not authenticated
  if (!user) {
    return noUserContent;
  }

  // Render user-specific content if authenticated
  return userContent(user);
}
